import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getOrder, getProducts, getWorkersByShop, updateOrder } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// Greek-friendly normalize for search
const normalize = (str) => (str || '').toString().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '');

// Format a Date for <input type="datetime-local"> (yyyy-MM-dd'T'HH:mm)
const formatLocal = (d) => format(d, "yyyy-MM-dd'T'HH:mm");

const STATUS_LABELS = {
  NEW: 'Νέα',
  IN_PROGRESS: 'Σε εξέλιξη',
  READY: 'Έτοιμη',
  DELIVERED: 'Παραδόθηκε',
  CANCELLED: 'Ακυρώθηκε',
};

// Fallback copy if the backend didn't supply a message
const localBlockedMessage = (id, status) => {
  if (status === 'IN_PROGRESS') return `Η παραγγελία #${id} ξεκίνησε ήδη στο εργοστάσιο («Σε εξέλιξη») και δεν μπορεί να τροποποιηθεί. Αν χρειάζεται αλλαγή, επικοινωνήστε με το εργοστάσιο.`;
  if (status === 'READY') return `Η παραγγελία #${id} είναι ήδη έτοιμη και δεν μπορεί να τροποποιηθεί. Αν χρειάζεται αλλαγή, επικοινωνήστε με το εργοστάσιο.`;
  if (status === 'DELIVERED') return `Η παραγγελία #${id} έχει παραδοθεί και δεν μπορεί να τροποποιηθεί.`;
  if (status === 'CANCELLED') return `Η παραγγελία #${id} έχει ακυρωθεί και δεν μπορεί να τροποποιηθεί.`;
  return `Η παραγγελία #${id} δεν μπορεί να τροποποιηθεί.`;
};

export default function EditOrder() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Set when the order can no longer be edited — shows a full-page notice
  const [blocked, setBlocked] = useState(null); // { message, status }

  // Per-product cart: { [product_id]: { quantity: string, notes: string } }
  const [cart, setCart] = useState({});

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [requestedFor, setRequestedFor] = useState('');

  // Prices/names the order was originally saved with. Items already in the
  // order keep their quoted price; only newly added products use today's price.
  const [originalItems, setOriginalItems] = useState(new Map());

  const loadEverything = useCallback(async () => {
    setLoading(true);
    try {
      const oRes = await getOrder(id);
      const o = oRes.data;

      if (o.status !== 'NEW') {
        setOrder(o);
        setBlocked({ message: localBlockedMessage(o.id, o.status), status: o.status });
        setLoading(false);
        return;
      }

      const origin = new Map();
      for (const it of o.items || []) {
        if (it.product_id === null || it.product_id === undefined) continue;
        origin.set(it.product_id, {
          product_name: it.product_name,
          unit_price: parseFloat(it.unit_price),
          factory_id: it.factory_id,
          factory_name: it.factory_name,
        });
      }

      const prefill = {};
      for (const it of o.items || []) {
        if (it.product_id === null || it.product_id === undefined) continue;
        prefill[it.product_id] = {
          quantity: String(parseFloat(it.quantity)),
          notes: it.notes || '',
          factory_id: it.factory_id,
        };
      }

      // An order can span factories now, so the edit page offers every
      // product, not just one factory's.
      const [pRes, wRes] = await Promise.all([
        getProducts({ includeInactive: false, shopId: o.shop_id }),
        getWorkersByShop(o.shop_id),
      ]);

      setOrder(o);
      setOriginalItems(origin);
      setCart(prefill);
      setProducts(pRes.data);
      setWorkers(wRes.data);
      setCustomerName(o.customer_name || '');
      setCustomerPhone(o.customer_phone || '');
      setCustomerNotes(o.customer_notes || '');
      setRequestedFor(formatLocal(new Date(o.requested_for)));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης');
      navigate('/my-orders');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadEverything(); }, [loadEverything]);

  const parseQty = (v) => {
    if (v === '' || v === null || v === undefined) return 0;
    const n = parseFloat(String(v).replace(',', '.'));
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const setQty = (productId, value) => {
    setCart((c) => {
      const cur = c[productId] || {};
      // A newly added product needs a factory; default to its first one
      let factoryId = cur.factory_id;
      if (parseQty(value) > 0 && !factoryId) {
        const p = products.find((x) => x.id === productId);
        if (p && p.factories && p.factories.length > 0) factoryId = p.factories[0].id;
      }
      return { ...c, [productId]: { ...cur, quantity: value, factory_id: factoryId } };
    });
  };
  const setFactory = (productId, factoryId) => {
    setCart((c) => ({ ...c, [productId]: { ...(c[productId] || {}), factory_id: Number(factoryId) } }));
  };
  const setNotes = (productId, notes) => {
    setCart((c) => ({ ...c, [productId]: { ...(c[productId] || {}), notes } }));
  };

  // The products available for this order's factory, PLUS any product that was
  // already in the order but is no longer offered (deactivated or unassigned).
  // Those can be kept or removed but are flagged.
  const selectableProducts = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, { ...p, orphan: false }]));
    for (const [pid, info] of originalItems.entries()) {
      if (!byId.has(pid)) {
        byId.set(pid, {
          id: pid,
          name: info.product_name,
          price: info.unit_price,
          factories: [],
          orphan: true,
        });
      }
    }
    // Orphans first so they're impossible to miss
    return [...byId.values()].sort((a, b) => (b.orphan ? 1 : 0) - (a.orphan ? 1 : 0));
  }, [products, originalItems]);

  // Matches the product name, its code, or its category name
  const filteredProducts = useMemo(() => {
    if (!search.trim()) return selectableProducts;
    const q = normalize(search);
    return selectableProducts.filter((p) =>
      normalize(p.name).includes(q)
      || (p.code && normalize(p.code).includes(q))
      || (p.category_name && normalize(p.category_name).includes(q))
    );
  }, [selectableProducts, search]);

  // Effective unit price: original snapshot if the product was already in the
  // order, otherwise the product's current price
  const priceOf = useCallback((p) => {
    const orig = originalItems.get(p.id);
    return orig ? orig.unit_price : parseFloat(p.price);
  }, [originalItems]);

  const cartItems = useMemo(() => {
    const items = [];
    for (const p of selectableProducts) {
      const entry = cart[p.id];
      if (!entry) continue;
      const qty = parseQty(entry.quantity);
      if (qty <= 0) continue;
      const unit = priceOf(p);
      const orig = originalItems.get(p.id);
      const factoryId = orig ? orig.factory_id : entry.factory_id;
      items.push({
        product_id: p.id,
        product_name: p.name,
        unit_price: unit,
        quantity: qty,
        factory_id: factoryId,
        factory_name: orig
          ? orig.factory_name
          : ((p.factories || []).find((f) => f.id === factoryId) || {}).name || '?',
        notes: entry.notes || '',
        total: unit * qty,
        wasOriginal: !!orig,
      });
    }
    return items;
  }, [selectableProducts, cart, priceOf, originalItems]);

  const grandTotal = cartItems.reduce((s, it) => s + it.total, 0);

  const originalTotal = order ? parseFloat(order.grand_total) : 0;
  const totalChanged = order && Math.abs(grandTotal - originalTotal) > 0.004;

  const submit = async () => {
    if (!workerId) return toast.error('Επιλέξτε εργαζόμενο');
    if (!requestedFor) return toast.error('Συμπληρώστε ημερομηνία/ώρα');
    if (cartItems.length === 0) return toast.error('Το καλάθι είναι άδειο — για να ακυρώσετε την παραγγελία χρησιμοποιήστε το κουμπί Ακύρωση');
    const missing = cartItems.find((it) => !it.factory_id);
    if (missing) return toast.error(`Επιλέξτε εργοστάσιο για «${missing.product_name}»`);

    setSubmitting(true);
    try {
      await updateOrder(id, {
        edited_by_worker_id: Number(workerId),
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim() || null,
        customer_notes: customerNotes.trim() || null,
        // Wall-clock string, not UTC — see the note in NewOrder.js
        requested_for: requestedFor,
        items: cartItems.map((it) => ({
          product_id: it.product_id,
          factory_id: it.factory_id,
          quantity: it.quantity,
          notes: it.notes || null,
        })),
      });
      toast.success('Οι αλλαγές αποθηκεύτηκαν!');
      navigate('/my-orders');
    } catch (err) {
      // 409 = the factory started it while this form was open
      if (err.response?.status === 409) {
        const status = err.response?.data?.current_status;
        setBlocked({
          message: err.response?.data?.error || localBlockedMessage(id, status),
          status,
        });
      } else {
        toast.error(err.response?.data?.error || 'Σφάλμα αποθήκευσης');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ───────────────────────── Blocked notice ─────────────────────────
  if (blocked) {
    return (
      <>
        <div className="page-header">
          <h2>✏️ Επεξεργασία Παραγγελίας #{id}</h2>
        </div>
        <div className="page-body">
          <div className="card">
            <div className="card-body">
              <div style={{
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: 10,
                padding: '20px 22px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}>
                <div style={{ fontSize: 34, lineHeight: 1 }}>⚠️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#92400e', fontSize: 16, marginBottom: 6 }}>
                    Δεν είναι δυνατή η τροποποίηση
                  </div>
                  <div style={{ color: '#78350f', fontSize: 14, lineHeight: 1.55 }}>
                    {blocked.message}
                  </div>
                  {blocked.status && (
                    <div style={{ marginTop: 10, fontSize: 13, color: '#92400e' }}>
                      Τρέχουσα κατάσταση: <strong>{STATUS_LABELS[blocked.status] || blocked.status}</strong>
                    </div>
                  )}
                  <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
                    <Link to="/my-orders" className="btn btn-primary">← Οι Παραγγελίες μου</Link>
                    <button className="btn btn-secondary" onClick={() => window.open(`/print/${id}`, '_blank')}>
                      🖨 Εκτύπωση
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading || !order) {
    return (
      <>
        <div className="page-header">
          <h2>✏️ Επεξεργασία Παραγγελίας #{id}</h2>
        </div>
        <div className="page-body">
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>✏️ Επεξεργασία Παραγγελίας #{order.id}</h2>
            <p>
              🏪 {order.shop_name} → 🏭 <strong>
                {[...new Set((order.items || []).map((i) => i.factory_name).filter(Boolean))].join(', ') || '—'}
              </strong>
              {order.edit_count > 0 && (
                <span style={{ marginLeft: 8, color: 'var(--gray-500)' }}>
                  · τροποποιήθηκε {order.edit_count} {order.edit_count === 1 ? 'φορά' : 'φορές'}
                </span>
              )}
            </p>
          </div>
          <Link to="/my-orders" className="btn btn-secondary">← Πίσω</Link>
        </div>
      </div>

      <div className="page-body">
        <div style={{
          background: 'var(--blue-light, #eff6ff)',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 13,
          color: '#1e40af',
          marginBottom: 16,
        }}>
          ℹ️ Η τροποποίηση επιτρέπεται μόνο όσο <strong>κανένα εργοστάσιο δεν έχει ξεκινήσει</strong>.
          Αν κάποιο ξεκινήσει όσο συμπληρώνετε τη φόρμα, η αποθήκευση θα απορριφθεί και θα χρειαστεί
          να επικοινωνήσετε μαζί τους. Μπορείτε να προσθέσετε προϊόντα από οποιοδήποτε εργοστάσιο —
          παραμένει μία παραγγελία.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 20, alignItems: 'start' }}>

          {/* LEFT: products */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Προϊόντα</span>
              <input
                className="form-input"
                placeholder="🔍 Αναζήτηση..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 220, padding: '6px 12px', margin: 0 }}
              />
            </div>
            <div className="card-body">
              {filteredProducts.length === 0 ? (
                <div className="empty-state"><div>🥐</div><p>Δεν βρέθηκαν προϊόντα</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {filteredProducts.map((p, i) => {
                    const entry = cart[p.id] || {};
                    const qty = parseQty(entry.quantity);
                    const unit = priceOf(p);
                    const keptPrice = originalItems.has(p.id) &&
                      !p.orphan &&
                      Math.abs(parseFloat(p.price) - unit) > 0.004;
                    // Products arrive sorted by category, then by the order the
                    // admin dragged into place, so a header goes in wherever
                    // the group changes. Orphans get a group of their own.
                    const groupKey = (x) => (x.orphan ? '__orphan__' : (x.category_id ?? 'none'));
                    const prev = i > 0 ? filteredProducts[i - 1] : null;
                    const header = (!prev || groupKey(prev) !== groupKey(p)) ? (
                      <div key={`h-${groupKey(p)}`} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 12px', marginTop: i === 0 ? 0 : 8,
                        background: p.orphan ? '#fef3c7' : 'var(--gray-100)',
                        borderRadius: 8, fontWeight: 700, fontSize: 13,
                        color: p.orphan ? '#92400e' : 'var(--gray-700)',
                      }}>
                        {p.orphan
                          ? <>⚠️ Εκτός καταλόγου</>
                          : (p.category_name ? <>🗂 {p.category_name}</> : <>📦 Χωρίς κατηγορία</>)}
                      </div>
                    ) : null;
                    const row = (
                      <div key={p.id} style={{
                        display: 'flex', flexDirection: 'column', gap: 6,
                        padding: '10px 12px',
                        border: p.orphan ? '1px solid #fcd34d' : '1px solid var(--gray-100)',
                        borderRadius: 8,
                        background: p.orphan ? '#fffbeb' : (qty > 0 ? '#f0f9ff' : 'white'),
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, color: 'var(--gray-800)' }}>
                              {p.code && (
                                <span style={{
                                  fontSize: 11, fontWeight: 700, color: 'var(--gray-500)',
                                  background: 'var(--gray-100)', borderRadius: 4,
                                  padding: '1px 6px', marginRight: 6,
                                }}>{p.code}</span>
                              )}
                              {p.name}
                              {p.orphan && (
                                <span className="badge badge-amber" style={{ marginLeft: 8, fontSize: 10 }}>
                                  μη διαθέσιμο πλέον
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                              €{unit.toFixed(2)}
                              {originalItems.has(p.id)
                                ? <> · 🏭 {originalItems.get(p.id).factory_name}</>
                                : (p.factories || []).length === 1
                                  ? <> · 🏭 {p.factories[0].name}</>
                                  : (p.factories || []).length > 1
                                    ? <> · 🏭 {p.factories.length} εργοστάσια</>
                                    : null}
                              {keptPrice && (
                                <span style={{ marginLeft: 6, color: '#b45309' }}>
                                  (αρχική τιμή · σήμερα €{parseFloat(p.price).toFixed(2)})
                                </span>
                              )}
                            </div>
                          </div>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            placeholder="0"
                            value={entry.quantity ?? ''}
                            onChange={(e) => setQty(p.id, e.target.value)}
                            className="form-input"
                            style={{ width: 80, padding: '6px 10px', margin: 0, textAlign: 'right' }}
                          />
                          {qty > 0 && (
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue-dark)', minWidth: 70, textAlign: 'right' }}>
                              €{(qty * unit).toFixed(2)}
                            </div>
                          )}
                        </div>
                        {qty > 0 && !originalItems.has(p.id) && (p.factories || []).length > 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>🏭</span>
                            <select
                              value={entry.factory_id || ''}
                              onChange={(e) => setFactory(p.id, e.target.value)}
                              style={{ flex: 1, padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--gray-200)' }}
                            >
                              {(p.factories || []).map((f) => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {p.orphan && (
                          <div style={{ fontSize: 11, color: '#92400e', paddingLeft: 4 }}>
                            Το προϊόν δεν προσφέρεται πλέον από αυτό το εργοστάσιο. Μπορείτε να το
                            κρατήσετε ή να το αφαιρέσετε (ποσότητα 0), αλλά όχι να το ξαναπροσθέσετε.
                          </div>
                        )}
                        {qty > 0 && (
                          <input
                            placeholder="Σημείωση για αυτό το προϊόν (προαιρετικά)"
                            value={entry.notes || ''}
                            onChange={(e) => setNotes(p.id, e.target.value)}
                            className="form-input"
                            style={{ padding: '4px 8px', fontSize: 12, margin: 0 }}
                          />
                        )}
                      </div>
                    );
                    return header ? [header, row] : row;
                  })}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: customer + summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Στοιχεία Πελάτη</span></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Τροποποιήθηκε από *</label>
                  <select className="form-select" value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
                    <option value="">-- Επιλέξτε εργαζόμενο --</option>
                    {workers.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Όνομα πελάτη *</label>
                  <input className="form-input" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Τηλέφωνο</label>
                  <input className="form-input" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Ημερομηνία/ώρα παραλαβής *</label>
                  <input type="datetime-local" className="form-input" value={requestedFor} onChange={(e) => setRequestedFor(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Σημειώσεις</label>
                  <textarea className="form-textarea" rows="2" value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Σύνοψη</span></div>
              <div className="card-body" style={{ padding: '14px 16px' }}>
                {cartItems.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--gray-400)', textAlign: 'center', padding: 12 }}>
                    Άδειο καλάθι
                  </div>
                ) : (
                  <>
                    {cartItems.map((it) => (
                      <div key={it.product_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-600)', padding: '3px 0' }}>
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {!it.wasOriginal && <span style={{ color: '#059669', fontWeight: 700 }}>+ </span>}
                          {it.product_name} × {it.quantity.toLocaleString('el-GR', { maximumFractionDigits: 3 })}
                        </span>
                        <span style={{ marginLeft: 8 }}>€{it.total.toFixed(2)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: 'var(--blue-dark)', borderTop: '2px solid var(--gray-200)', marginTop: 8, paddingTop: 8 }}>
                      <span>ΣΥΝΟΛΟ</span>
                      <span>€{grandTotal.toFixed(2)}</span>
                    </div>
                    {totalChanged && (
                      <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'right', marginTop: 4 }}>
                        ήταν €{originalTotal.toFixed(2)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <button
              className="btn btn-primary btn-block"
              style={{ padding: '12px 16px', fontSize: 15 }}
              disabled={submitting || cartItems.length === 0 || !requestedFor || !workerId}
              onClick={submit}
            >
              {submitting ? <span className="loading-spinner" /> : '💾 Αποθήκευση Αλλαγών'}
            </button>

            <Link to="/my-orders" className="btn btn-secondary btn-block" style={{ textAlign: 'center' }}>
              Άκυρο
            </Link>
          </div>

        </div>
      </div>
    </>
  );
}
